import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, Settings, BookOpen } from 'lucide-react';
import { useDashboardStore } from '../../store/dashboardStore';
import { useMediaQuery } from '../../hooks/useMediaQuery';

export const IconSidebar: React.FC = () => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const {
    currentUser,
    showWorkflowStaffListPanel,
    showDatePanel,
    showSettingsPanel,
    showStaffListPanel,
    showAllPatientListPanel,
    showPatientFormPanel,
    showStaffFormPanel,
    showSettingsDatePanel,
    showSchedulePanel,
    showSchedulePatientListPanel,
    schedulePanelSource,
    showSpeechDictionaryPanel,
    openSpeechDictionaryPanel,
    // ▼▼▼ 閉じるべきパネルのセッターをすべて取得 ▼▼▼
    setShowDatePanel,
    setShowSettingsPanel,
    setShowAllPatientListPanel,
    setShowStaffListPanel,
    setShowPatientFormPanel,
    setShowStaffFormPanel, // スタッフフォームを閉じるための関数
    setShowDetailPanel,
    setShowSettingsDatePanel,
    setShowSchedulePanel,
    setShowSchedulePatientListPanel,
    setShowWorkflowStaffListPanel,
  } = useDashboardStore();

  const isSettingsLaneActive =
    showSettingsPanel ||
    showStaffListPanel ||
    showAllPatientListPanel ||
    showPatientFormPanel ||
    showStaffFormPanel ||
    showSettingsDatePanel ||
    (showSchedulePanel && schedulePanelSource === 'settings') ||
    (showSchedulePatientListPanel && schedulePanelSource === 'settings');

  const handleSettingsClick = () => {
    // 診療・辞書を含む全パネルを一括で閉じ、設定を開く（single setState）。
    // 診療→設定方向は残像が出ないため rAF 不要。
    useDashboardStore.setState((s) => ({
      showDatePanel: false,
      showWorkflowStaffListPanel: false,
      showDetailPanel: false,
      detailPanelRevision: s.detailPanelRevision + (s.showDetailPanel ? 1 : 0),
      showSchedulePanel: false,
      schedulePanelRevision: s.schedulePanelRevision + (s.showSchedulePanel ? 1 : 0),
      showSchedulePatientListPanel: false,
      showAllPatientListPanel: false,
      showStaffListPanel: false,
      showPatientFormPanel: false,
      showStaffFormPanel: false,
      showSettingsDatePanel: false,
      showSpeechDictionaryPanel: false,
      showSettingsPanel: true,
      settingsPanelRevision: s.settingsPanelRevision + 1,
    }));
  };

  const handleDictionaryClick = () => {
    openSpeechDictionaryPanel();
  };

  const handleWorkflowClick = () => {
    const state = useDashboardStore.getState();

    const settingsLaneOpen =
      state.showSettingsPanel ||
      state.showStaffListPanel ||
      state.showAllPatientListPanel ||
      state.showPatientFormPanel ||
      state.showStaffFormPanel ||
      state.showSettingsDatePanel ||
      (state.showSchedulePanel && state.schedulePanelSource === 'settings') ||
      (state.showSchedulePatientListPanel && state.schedulePanelSource === 'settings');

    const isStaff = currentUser?.role === 'staff';

    // 辞書のみ表示中（診療・設定レーンのフラグは閉じている）: 先に辞書を閉じ、次フレームで診療を開く
    const workflowPanelsOpen =
      state.showWorkflowStaffListPanel ||
      state.showDatePanel ||
      state.showDetailPanel ||
      (state.showSchedulePanel && state.schedulePanelSource === 'workflow') ||
      (state.showSchedulePatientListPanel && state.schedulePanelSource === 'workflow');
    // 辞書のみ・または設定レーン表示中 → 先に閉じ、次フレームで診療を開く（rAF 1フレームで十分: PC は exit アニメなし即座消滅）
    if ((state.showSpeechDictionaryPanel && !settingsLaneOpen && !workflowPanelsOpen) || settingsLaneOpen) {
      setShowDetailPanel(false);
      setShowSchedulePanel(false);
      setShowSchedulePatientListPanel(false);
      setShowSettingsPanel(false);
      setShowStaffListPanel(false);
      setShowAllPatientListPanel(false);
      setShowPatientFormPanel(false);
      setShowStaffFormPanel(false);
      setShowSettingsDatePanel(false);
      useDashboardStore.setState({ showSpeechDictionaryPanel: false });
      requestAnimationFrame(() => {
        const s2 = useDashboardStore.getState();
        if (isStaff) {
          setShowWorkflowStaffListPanel(false);
          setShowDatePanel(true);
        } else {
          if (s2.showWorkflowStaffListPanel) {
            useDashboardStore.setState((t) => ({
              workflowStaffListRevision: t.workflowStaffListRevision + 1,
            }));
          } else {
            setShowWorkflowStaffListPanel(true);
          }
          setShowDatePanel(false);
        }
      });
      return;
    }

    if (isStaff) {
      setShowWorkflowStaffListPanel(false);
      setShowDatePanel(true);
    } else {
      if (state.showWorkflowStaffListPanel) {
        useDashboardStore.setState((s) => ({
          workflowStaffListRevision: s.workflowStaffListRevision + 1,
        }));
      } else {
        setShowWorkflowStaffListPanel(true);
      }
      setShowDatePanel(false);
    }
    setShowDetailPanel(false);
    setShowSchedulePanel(false);
    setShowSchedulePatientListPanel(false);

    setShowSettingsPanel(false);
    setShowStaffListPanel(false);
    setShowAllPatientListPanel(false);
    setShowPatientFormPanel(false);
    setShowStaffFormPanel(false);
    setShowSettingsDatePanel(false);
    useDashboardStore.setState({ showSpeechDictionaryPanel: false });
  };

  // スマホ版: アイコンはフッターに移動済み。中央は空
  if (isMobile) {
    return <div className="flex-1" />;
  }

  // デスクトップ版: サイドバーとして表示
  return (
    <div className="flex flex-col justify-between items-center w-20 bg-white border-r py-4">
      {/* 上のアイコン (診療フロー) */}
      <motion.button
        onClick={handleWorkflowClick}
        className={`p-3 rounded-lg transition-colors ${
          (showWorkflowStaffListPanel || (currentUser?.role === 'staff' && showDatePanel)) ? 'text-white' : 'hover:bg-gray-100 text-gray-600'
        }`}
        style={(showWorkflowStaffListPanel || (currentUser?.role === 'staff' && showDatePanel)) ? { backgroundColor: '#00a48d' } : {}}
      >
        <Calendar size={24} />
      </motion.button>

      <div className="flex flex-col gap-2 items-center">
        <motion.button
          onClick={handleDictionaryClick}
          className={`p-3 rounded-lg transition-colors ${
            showSpeechDictionaryPanel ? 'text-white' : 'hover:bg-gray-100 text-gray-600'
          }`}
          style={showSpeechDictionaryPanel ? { backgroundColor: '#00a48d' } : {}}
          aria-label="音声用語辞書"
        >
          <BookOpen size={24} />
        </motion.button>
        {(currentUser?.role === 'admin' || currentUser?.role === 'developer') && (
          <motion.button
            onClick={handleSettingsClick}
            className={`p-3 rounded-lg transition-colors ${
              isSettingsLaneActive ? 'text-white' : 'hover:bg-gray-100 text-gray-600'
            }`}
            style={isSettingsLaneActive ? { backgroundColor: '#00a48d' } : {}}
            aria-label="設定"
          >
            <Settings size={24} />
          </motion.button>
        )}
      </div>
    </div>
  );
};
